import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Row,
  Col,
  Space,
  Switch,
  Tooltip,
  Typography,
  message,
  Select,
  Table,
  Modal,
  Popconfirm,
  Tag,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  getAllChatbotConfigs,
  createChatbotConfig,
  updateChatbotConfig,
  deleteChatbotConfig,
} from "../../api/chatbot/ChatbotApi";

const { Title, Text } = Typography;

function ChatbotSettings() {
  const [form] = Form.useForm();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  async function loadConfigs() {
    setLoading(true);
    try {
      const res = await getAllChatbotConfigs();
      setConfigs(res || []);
    } catch (e) {
      message.error('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true, botType: 'AYD' });
    setModalVisible(true);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    form.setFieldsValue({
      chatbotid: config.chatbotid,
      apiKey: config.apiKey,
      enabled: config.enabled,
      name: config.name,
      email: config.email,
      botType: config.botType || 'AYD',
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteChatbotConfig(id);
      message.success('Configuration deleted');
      loadConfigs();
    } catch (e) {
      message.error(e?.message || 'Failed to delete configuration');
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const payload = {
        chatbotid: values.chatbotid,
        apiKey: values.apiKey,
        enabled: values.enabled ? true : false,
        name: values.name || null,
        email: values.email || null,
        botType: values.botType || 'AYD',
      };

      if (editingConfig) {
        await updateChatbotConfig(editingConfig.id, payload);
        message.success('Configuration updated');
      } else {
        await createChatbotConfig(payload);
        message.success('Configuration created');
      }

      setModalVisible(false);
      form.resetFields();
      loadConfigs();
    } catch (e) {
      message.error(e?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Bot Type',
      dataIndex: 'botType',
      key: 'botType',
      render: (type) => {
        const typeConfig = {
          'GEM': { color: 'blue', label: 'GEM (Strategic Advisor)' },
          'AYD': { color: 'green', label: 'AYD (Ask Your Database)' }
        };
        const config = typeConfig[type] || { color: 'default', label: type };
        return (
          <Tag color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name || '-',
    },
    {
      title: 'Chatbot ID / Model',
      dataIndex: 'chatbotid',
      key: 'chatbotid',
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this configuration?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4} style={{ marginBottom: 0 }}>Chatbot Configurations</Title>
            <Text type="secondary">
              Configure AI chatbot credentials. Supports OpenAI (GPT-4, GPT-4o-mini) for GEM and AskYourDatabase for AYD.
            </Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add Configuration
          </Button>
        </div>

        <Card>
          <Table
            dataSource={configs}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </Card>
      </Space>

      <Modal
        title={editingConfig ? 'Edit Configuration' : 'Add Configuration'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          initialValues={{ enabled: true, botType: 'AYD' }}
        >
          <Form.Item
            label="Bot Type"
            name="botType"
            rules={[{ required: true, message: "Please select bot type" }]}
          >
            <Select placeholder="Select bot type">
              <Select.Option value="AYD">AYD (Ask Your Database)</Select.Option>
              <Select.Option value="GEM">GEM (Strategic Advisor)</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>Chatbot ID / Model</span>
                <Tooltip title="For AYD: Enter your AskYourDatabase chatbot ID. For GEM: Enter OpenAI model name like 'openai-gpt4o-mini' or 'openai-gpt4'.">
                  <span className="text-gray-400 cursor-help">(?)</span>
                </Tooltip>
              </Space>
            }
            name="chatbotid"
            rules={[{ required: true, message: "Please enter Chatbot ID or Model" }]}
          >
            <Input placeholder="For AYD: chatbot ID, For GEM: openai-gpt4o-mini" allowClear />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>API Key</span>
                <Tooltip title="For AYD: Bearer API key from AskYourDatabase. For GEM: OpenAI API key starting with sk-...">
                  <span className="text-gray-400 cursor-help">(?)</span>
                </Tooltip>
              </Space>
            }
            name="apiKey"
            rules={[{ required: true, message: "Please enter API Key" }]}
          >
            <Input.Password placeholder="Enter API Key" visibilityToggle allowClear />
          </Form.Item>

          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Divider style={{ margin: "12px 0" }} />

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Display Name" name="name">
                <Input placeholder="Organization or bot name" allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Email" name="email" rules={[{ type: "email", message: "Enter a valid email" }]}>
                <Input placeholder="contact@example.com" allowClear />
              </Form.Item>
            </Col>
          </Row>

          <Space>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editingConfig ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => {
              setModalVisible(false);
              form.resetFields();
            }}>
              Cancel
            </Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}

export default ChatbotSettings;
